import React, { useState, createContext } from 'react';
import { API } from 'aws-amplify';
import { useAuth } from './Auth';
import { useApiError } from './ApiErrorProvider';
import { error } from '../constants/bindings';
import { SyncDelete } from '../models/SettingsPage';
import { SettingsSync } from '../models/SyncToCalendar';

const SyncedCalendarDefault: any = [];
const SettingsContext = createContext({
  syncToCalLoading: true,
  syncedCalendarData: SyncedCalendarDefault as SettingsSync[],
  getSyncedCalendarData: () => {},
  deleteSyncedCalendarData: (syncDelete: SyncDelete) => {},
  clearNotificationLoading: true,
  clearNotification: (emailId, accountType) => {}
});

export const SettingsProvider = (props) => {
  const { user } = useAuth();
  const { setErrorCode } = useApiError();
  const [syncToCalLoading, setSyncToCalLoading] = useState(false);
  const [clearNotificationLoading, setClearNotificationLoading] =
    useState(false);
  const [syncedCalendarData, setSyncedCalendarData] = useState<SettingsSync[]>(
    SyncedCalendarDefault
  );

  const getSyncedCalendarData = async () => {
    if (!user) {
      return;
    }
    try {
      setSyncToCalLoading(true);
      const syncedCalendarData = await API.get(
        'crewlife',
        `/calendarapi/accounts`,
        {}
      );
      setSyncedCalendarData(syncedCalendarData.results);
      setSyncToCalLoading(false);
      return syncedCalendarData;
    } catch (e: any) {
      setSyncToCalLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    }
  };

  const deleteSyncedCalendarData = async (syncDelete: SyncDelete) => {
    if (!syncDelete || !user) {
      return;
    }
    try {
      setSyncToCalLoading(true);
      const requestObject = {
        body: {
          ...syncDelete
        }
      };
      await API.post('crewlife', '/calendarapi/syncaccounts', requestObject);
      setSyncToCalLoading(false);
    } catch (e: any) {
      setSyncToCalLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    } finally {
      setSyncToCalLoading(false);
    }
  };

  const clearNotification = async (emailId, accountType) => {
    if (!emailId || !accountType || !user) {
      return;
    }
    try {
      setClearNotificationLoading(true);
      const requestObject = {
        body: {
          emailId: emailId,
          accountType: accountType
        }
      };
      await API.post(
        'crewlife',
        '/calendarapi/clearnotification',
        requestObject
      );
      setClearNotificationLoading(false);
    } catch (e: any) {
      setClearNotificationLoading(false);
      if (e.response?.status) {
        setErrorCode(e.response?.status);
      } else {
        setErrorCode(error.DEFAULT);
      }
    } finally {
      setClearNotificationLoading(false);
    }
  };

  return (
    <SettingsContext.Provider
      value={{
        syncToCalLoading,
        syncedCalendarData,
        getSyncedCalendarData,
        deleteSyncedCalendarData,
        clearNotificationLoading,
        clearNotification
      }}
    >
      {props.children}
    </SettingsContext.Provider>
  );
};

export const useSettingsPageProvider = () => {
  return React.useContext(SettingsContext);
};
